import {
  BookkeepingDefaultAccounts,
  VatRateAccountMapping,
} from "../types/BookkeepingDefaultAccounts";
import { COUNTRY_BOOKKEEPING_DEFAULTS } from "../types/defaults/defaultBookkeepingAccounts";
import { Country } from "../types/enums/Country";
import { PaymentMethodType } from "../types/enums/PaymentMethodType";

const getCountryDefaultMapping = (
  country: Country,
): BookkeepingDefaultAccounts => {
  return COUNTRY_BOOKKEEPING_DEFAULTS[country];
};

const mergeMappings = (
  base: BookkeepingDefaultAccounts,
  override: Partial<BookkeepingDefaultAccounts>,
): BookkeepingDefaultAccounts => {
  const mergedVatRateMappings = new Map<number, VatRateAccountMapping>();

  base.vatRateMappings.forEach((mapping: VatRateAccountMapping) => {
    mergedVatRateMappings.set(mapping.vatRate, mapping);
  });

  override.vatRateMappings?.forEach((mapping: VatRateAccountMapping) => {
    mergedVatRateMappings.set(mapping.vatRate, mapping);
  });

  return {
    vatRateMappings: Array.from(mergedVatRateMappings.values()),
    businessAccount: override.businessAccount ?? base.businessAccount,
    roundingAccount: override.roundingAccount ?? base.roundingAccount,
    tipAccount: override.tipAccount ?? base.tipAccount,
    bankFeesAccount: override.bankFeesAccount ?? base.bankFeesAccount,
    giftCardAccount: override.giftCardAccount ?? base.giftCardAccount,
    giftCardInterStoreDebtAccount:
      override.giftCardInterStoreDebtAccount ??
      base.giftCardInterStoreDebtAccount,
    creditNoteAccount: override.creditNoteAccount ?? base.creditNoteAccount,
    creditNoteInterStoreDebtAccount:
      override.creditNoteInterStoreDebtAccount ??
      base.creditNoteInterStoreDebtAccount,
    expiredGiftcardAccount:
      override.expiredGiftcardAccount ?? base.expiredGiftcardAccount,
    expiredCreditNoteAccount:
      override.expiredCreditNoteAccount ?? base.expiredCreditNoteAccount,
    serviceOrderDepositAccount:
      override.serviceOrderDepositAccount ?? base.serviceOrderDepositAccount,
    forfeitedServiceOrderDepositAccount:
      override.forfeitedServiceOrderDepositAccount ??
      base.forfeitedServiceOrderDepositAccount,
    purchaseAccount: override.purchaseAccount ?? base.purchaseAccount,
  };
};

export const getServiceOrderDepositAccount = (
  defaults: BookkeepingDefaultAccounts,
) => defaults.serviceOrderDepositAccount ?? defaults.creditNoteAccount;

export const getForfeitedServiceOrderDepositAccount = (
  defaults: BookkeepingDefaultAccounts,
) =>
  defaults.forfeitedServiceOrderDepositAccount ??
  defaults.expiredCreditNoteAccount;

export const getPurchaseAccount = (defaults: BookkeepingDefaultAccounts) =>
  defaults.purchaseAccount ?? "4000";

export const getEffectiveBookkeepingMapping = (
  country: Country,
  bookkeepingDefaultAccounts?: Partial<BookkeepingDefaultAccounts> | null,
): BookkeepingDefaultAccounts => {
  const countryDefault = getCountryDefaultMapping(country);

  if (bookkeepingDefaultAccounts) {
    return mergeMappings(countryDefault, bookkeepingDefaultAccounts);
  }

  return countryDefault;
};

export const getDefaultBookkeepingAccountForPaymentMethod = (
  defaults: BookkeepingDefaultAccounts,
  payment: { type: PaymentMethodType; issuingOrgNumber?: string },
  store?: { orgNumber: string },
) => {
  if (payment.type === PaymentMethodType.CREDIT_NOTE) {
    if (
      store &&
      payment.issuingOrgNumber &&
      payment.issuingOrgNumber !== store.orgNumber
    ) {
      return (
        defaults.creditNoteInterStoreDebtAccount ?? defaults.creditNoteAccount
      );
    }
    return defaults.creditNoteAccount;
  }
  if (payment.type === PaymentMethodType.GIFT_CARD) {
    if (
      store &&
      payment.issuingOrgNumber &&
      payment.issuingOrgNumber !== store.orgNumber
    ) {
      return defaults.giftCardInterStoreDebtAccount ?? defaults.giftCardAccount;
    }
    return defaults.giftCardAccount;
  }
  return defaults.businessAccount;
};
